import { useQueryClient } from "@tanstack/react-query";
import { Loader2, LogOut, Mail, MailCheck, RefreshCw } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { authApi } from "@/features/auth/api";
import { useLogout } from "@/features/auth/hooks";
import { useAuthStore } from "@/features/auth/store";
import type { MeResponse } from "@/features/auth/types";
import { apiErrorMessage } from "@/lib/api/errors";

/**
 * Écran de blocage — addendum ADR 0033 (2026-09-30, demande d'Oumou).
 *
 * Le lien automatique de l'e-mail exigeait déjà la confirmation avant
 * d'entrer ; la connexion MANUELLE (e-mail + mot de passe), elle, laissait
 * toujours passer — constaté en se connectant ainsi sans jamais confirmer.
 * Cet écran ferme cette voie pour le même compte : tant que
 * `email_verified` est faux, AUCUN accès (ni lien, ni mot de passe), à
 * l'exception de `/profil` (déconnexion) — mêmes coordonnées que
 * `ProtectedLayout` pour `must_change_password`/licence expirée.
 *
 * Portée volontairement étroite : dans ce dépôt, seul un compte fraîchement
 * créé par l'inscription self-service peut avoir `email_verified_at` vide —
 * le personnel invité et la réinitialisation de mot de passe marquent déjà
 * l'adresse vérifiée en la prouvant (ADR 0033 point 4). Aucun compte
 * existant ne se retrouve donc bloqué par surprise.
 */
export function EmailNotVerifiedScreen() {
  const me = useAuthStore((s) => s.me);
  const logout = useLogout();
  const queryClient = useQueryClient();
  const [resending, setResending] = useState(false);
  const [checking, setChecking] = useState(false);
  const [sent, setSent] = useState(false);

  async function resend() {
    setResending(true);
    try {
      const r = await authApi.resendEmailVerification();
      setSent(true);
      toast.success(
        r.code === "already_verified"
          ? "Adresse déjà vérifiée — vérifiez à nouveau ci-dessous."
          : "E-mail de vérification envoyé. Ouvrez le lien reçu.",
      );
    } catch (err) {
      toast.error(apiErrorMessage(err, "L'e-mail n'a pas pu être envoyé."));
    } finally {
      setResending(false);
    }
  }

  /** Une fois le lien cliqué (peut-être dans un autre onglet), l'écran
   *  disparaît de lui-même si `/auth/me` revient vérifié. */
  async function recheck() {
    setChecking(true);
    try {
      await queryClient.refetchQueries({ queryKey: ["auth", "me"] });
      const fresh = queryClient.getQueryData<MeResponse>(["auth", "me"]);
      if (fresh && !fresh.email_verified) {
        toast.info("Toujours pas confirmée. Ouvrez le lien reçu par e-mail.");
      }
    } finally {
      setChecking(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
        <span className="flex items-center gap-2 text-sm text-foreground">
          <MailCheck className="h-5 w-5 text-primary" aria-hidden />
          <span className="font-semibold uppercase tracking-[0.2em]">MADA</span>
        </span>
        {me?.email && (
          <span className="max-w-[12rem] truncate text-xs text-muted-foreground">{me.email}</span>
        )}
      </header>

      <main className="flex flex-1 items-center justify-center px-4 py-8 sm:py-12">
        <div className="w-full max-w-md">
          <section className="overflow-hidden rounded border border-border bg-surface shadow-sm">
            <div className="flex items-start gap-4 border-b border-warning/20 bg-warning/5 p-5 sm:p-6">
              <span className="flex h-11 w-11 flex-none items-center justify-center rounded bg-warning/10 text-warning">
                <Mail className="h-6 w-6" aria-hidden />
              </span>
              <div className="min-w-0">
                <h1 className="text-lg font-semibold text-foreground sm:text-xl">
                  Confirmez votre adresse e-mail
                </h1>
                <p className="mt-0.5 truncate text-sm text-foreground">{me?.email}</p>
              </div>
            </div>

            <div className="space-y-4 p-5 sm:p-6">
              <p className="text-sm leading-relaxed text-muted-foreground">
                Un e-mail vous a été envoyé à la création de votre espace. Ouvrez-le et cliquez
                sur le lien de confirmation pour accéder à votre tableau de bord.
              </p>
              <Button className="w-full" onClick={resend} disabled={resending}>
                {resending ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                ) : (
                  <Mail className="h-4 w-4" aria-hidden />
                )}
                {sent ? "Renvoyer à nouveau" : "Renvoyer l'e-mail"}
              </Button>
            </div>

            <div className="flex flex-col gap-2 border-t border-border bg-background p-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
              <Button variant="outline" size="sm" onClick={recheck} disabled={checking}>
                {checking ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                ) : (
                  <RefreshCw className="h-4 w-4" aria-hidden />
                )}
                Vérifier à nouveau
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => logout.mutate()}
                disabled={logout.isPending}
              >
                <LogOut className="h-4 w-4" aria-hidden />
                Se déconnecter
              </Button>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
